import { FormBuilderEvent, NoteDataManager } from "../../note-data-manager";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { EnumsService } from "../../../../services/enums-service";
import { IGetWoundGroupBehavior } from "../../../../interfaces/form-builder/i-behavior";
import { IFormModel } from "../../../../interfaces/form-builder/i-model-schema";
import { IWoundBodyLocation } from "../../../../interfaces/i-wound-body";

export class WoundBodyStore {
    private readonly _enumsService: EnumsService;
    private readonly _noteDataManager: NoteDataManager;
    private readonly _ea: EventAggregator;
    private readonly _reset: (wounds: IWoundBodyLocation[]) => void;
    private readonly _listGroupName: string;
    private readonly _woundBodyBehavior: IGetWoundGroupBehavior;
    private readonly _subscription: Subscription[] = [];
    private _wounds: IWoundBodyLocation[] = [];
    private _noteModel: IFormModel;

    public constructor(
        enumsService: EnumsService,
        noteDataManager: NoteDataManager,
        ea: EventAggregator,
        woundBodyBehavior: IGetWoundGroupBehavior,
        listGroupName: string,
        reset: (wounds: IWoundBodyLocation[]) => void
    ) {
        this._enumsService = enumsService;
        this._noteDataManager = noteDataManager;
        this._ea = ea;
        this._reset = reset;
        this._woundBodyBehavior = woundBodyBehavior;
        this._listGroupName = listGroupName;
    }

    public initialize() {
        this._noteModel = this._noteDataManager.getNoteModel();
        Object.keys(this._noteModel).forEach((key) => this.addData(key));
        this.initSubscriptions();
    }

    private initSubscriptions() {
        this._subscription.push(this._ea.subscribe(FormBuilderEvent.PropDataUpdated, this.addData.bind(this)));
        this._subscription.push(
            this._ea.subscribe(FormBuilderEvent.ModifiedDataPropDeleted, this.deleteData.bind(this))
        );
    }

    private addData(prop: string) {
        if (!prop) {
            return;
        }
        let modifiedData = this._noteDataManager.getModifiedData();
        let propSplit = prop.split(".");
        const prefix = `${this._listGroupName}`;

        if (prop.startsWith(prefix)) {
            const relativePath = prop.substring(prefix.length);
            const pathParts = relativePath.split(".");
            if (pathParts.length >= 2) {
                let index = parseInt(pathParts[1], 10);

                if (!this._wounds[index]) {
                    this._wounds[index] = {};
                }
                let propertyKey = pathParts[2];
                let newValue = (modifiedData[prop] ?? this._noteModel[prop]) as string;
                newValue = newValue != null ? String(newValue) : newValue;
                if (
                    !newValue
                ) {
                    this.deleteData(prop);
                    return;
                }
                switch (propertyKey.toLowerCase()) {
                    case this._woundBodyBehavior.woundNumber.toLowerCase():
                        this._wounds[index].woundNumber = newValue;
                        break;
                    case this._woundBodyBehavior.xCoordinate.toLowerCase():
                        this._wounds[index].xCoordinate = newValue;
                        break;
                    case this._woundBodyBehavior.yCoordinate.toLowerCase():
                        this._wounds[index].yCoordinate = newValue;
                        break;
                    case this._woundBodyBehavior.woundSide.toLowerCase():
                        this._wounds[index].woundSide = newValue;
                        break;
                    case this._woundBodyBehavior.locationSearchType.toLowerCase():
                        this._wounds[index].woundLocationSearch = newValue;
                        break;
                }
                if (
                    this._wounds[index].woundSide &&
                    this._wounds[index].yCoordinate &&
                    this._wounds[index].xCoordinate &&
                    this._wounds[index].woundNumber
                ) {
                    this.rebuildWounds();
                    this._reset(this._wounds);
                }
            }
        }
    }

    private deleteData(prop: string) {
        if (!prop) {
            return;
        }
        let propSplit = prop.split(".");
        if (propSplit.length > 2 && prop.includes(this._listGroupName)) {
            let index = Number(propSplit[propSplit.length - 2]);
            let inputName = propSplit[propSplit.length - 1];
            switch (inputName.toLowerCase()) {
                case this._woundBodyBehavior.woundNumber.toLowerCase():
                    delete this._wounds[index].woundNumber;
                    break;
                case this._woundBodyBehavior.xCoordinate.toLowerCase():
                    delete this._wounds[index].xCoordinate;
                    break;
                case this._woundBodyBehavior.yCoordinate.toLowerCase():
                    delete this._wounds[index].yCoordinate;
                    break;
                case this._woundBodyBehavior.woundSide.toLowerCase():
                    delete this._wounds[index].woundSide;
                    break;
                case this._woundBodyBehavior.locationSearchType.toLowerCase():
                    delete this._wounds[index].woundLocationSearch;
                    break;
            }
            switch (inputName.toLowerCase()) {
                case this._woundBodyBehavior.woundNumber.toLowerCase():
                case this._woundBodyBehavior.xCoordinate.toLowerCase():
                case this._woundBodyBehavior.yCoordinate.toLowerCase():
                case this._woundBodyBehavior.woundSide.toLowerCase():
                case this._woundBodyBehavior.locationSearchType.toLowerCase():
                    this.rebuildWounds();
                    this._reset(this._wounds);
            }
        }
    }

    private rebuildWounds() {
        this._wounds.length = 0;
        const noteModel = this._noteDataManager.getNoteModel() ?? {};
        const modifiedData = this._noteDataManager.getModifiedData() ?? {};
        const allKeys = new Set([
            ...Object.keys(noteModel),
            ...Object.keys(modifiedData)
        ]);
        allKeys.forEach((prop) => {
            const propSplit = prop.split(".");
            if (propSplit.length <= 2 || !prop.includes(this._listGroupName)) {
                return;
            }
            const index = Number(propSplit[propSplit.length - 2]);
            if (isNaN(index)) {
                return;
            }
            const propertyKey = propSplit[propSplit.length - 1];
            let value = (modifiedData[prop] ?? noteModel[prop]) as string;
            if (value == null || String(value) === "") {
                return;
            }
            value = String(value);
            if (!this._wounds[index]) {
                this._wounds[index] = {} as IWoundBodyLocation;
            }
            switch (propertyKey.toLowerCase()) {
                case this._woundBodyBehavior.woundNumber.toLowerCase():
                    this._wounds[index].woundNumber = value;
                    break;
                case this._woundBodyBehavior.xCoordinate.toLowerCase():
                    this._wounds[index].xCoordinate = value;
                    break;
                case this._woundBodyBehavior.yCoordinate.toLowerCase():
                    this._wounds[index].yCoordinate = value;
                    break;
                case this._woundBodyBehavior.woundSide.toLowerCase():
                    this._wounds[index].woundSide = value;
                    break;
                case this._woundBodyBehavior.locationSearchType.toLowerCase():
                    this._wounds[index].woundLocationSearch = value;
                    break;
            }
        });
    }

    public detached() {
        if (this._subscription?.length > 0) {
            this._subscription.forEach((sub) => sub.dispose());
        }
    }
}
